export interface ValidationResult {
    isValid: boolean;
    error?: string;
    formattedValue?: string;
}

const DEFAULT_VALIDATION_ERROR = 'Vänligen ange ett värde med korrekt format';

// VIN/Chassis number validation (international standard)
export const validateChassisNumber = (value: string, customError?: string): ValidationResult => {
    const cleanValue = value.replace(/\s/g, '').toUpperCase();
    const errorMsg = customError ?? DEFAULT_VALIDATION_ERROR;

    // VIN must be exactly 17 characters
    if (cleanValue.length !== 17) {
        return { isValid: false, error: errorMsg };
    }

    // VIN can only contain letters (except I, O, Q) and numbers
    const vinPattern = /^[A-HJ-NPR-Z0-9]{17}$/;
    if (!vinPattern.test(cleanValue)) {
        return { isValid: false, error: errorMsg };
    }

    return {
        isValid: true,
        formattedValue: cleanValue
    };
};

const REGISTRATION_PATTERNS = {
    SE: {
        // SE formats: ABC123, ABC12A, XXX12X (three letters, two digits, one letter)
        pattern: /^[A-Z]{3}\d{3}$|^[A-Z]{3}\d{2}[A-Z]$/,
        maxLength: 6
    },
    DK: {
        pattern: /^[A-Z]{2}\d{5}$/,
        maxLength: 7
    },
    FI: {
        pattern: /^[A-Z]{3}\d{3}$/,
        maxLength: 6
    },
    NO: {
        pattern: /^[A-Z]{2}\d{5}$/,
        maxLength: 7
    }
};

export const validateRegistrationNumber = (value: string, countryCode: string, customError?: string): ValidationResult => {
    const cleanValue = value.replace(/\s/g, '').toUpperCase();
    const pattern = REGISTRATION_PATTERNS[countryCode as keyof typeof REGISTRATION_PATTERNS];
    const errorMsg = customError ?? DEFAULT_VALIDATION_ERROR;

    if (!cleanValue || !pattern || cleanValue.length > pattern.maxLength || !pattern.pattern.test(cleanValue)) {
        return { isValid: false, error: errorMsg };
    }

    return {
        isValid: true,
        formattedValue: cleanValue
    };
};

export const formatRegistrationNumber = (value: string, countryCode: string): string => {
    const cleanValue = value.replace(/\s/g, '').replace(/-/g, '').toUpperCase();

    // No special formatting - return clean value without dashes
    return cleanValue;
};

/** Format hints for i18n - keys must match valuation.registration */
export type RegistrationFormatHintLabels = {
    formatHintChassis: string;
    formatHintSE: string;
    formatHintDK: string;
    formatHintFI: string;
    formatHintNO: string;
    formatHintDefault: string;
};

export const getFormatHintLocalized = (
    countryCode: string,
    isChassisSearch: boolean,
    labels: RegistrationFormatHintLabels
): string => {
    if (isChassisSearch) return labels.formatHintChassis;
    switch (countryCode) {
        case 'SE': return labels.formatHintSE;
        case 'DK': return labels.formatHintDK;
        case 'FI': return labels.formatHintFI;
        case 'NO': return labels.formatHintNO;
        default: return labels.formatHintDefault;
    }
};

export const validateInput = (
    value: string,
    countryCode: string,
    isChassisSearch: boolean,
    customError?: string
): ValidationResult => {
    if (isChassisSearch) {
        return validateChassisNumber(value, customError);
    } else {
        return validateRegistrationNumber(value, countryCode, customError);
    }
};

// Simplified validation for registration numbers that supports all Nordic formats
// Used when country is not specified (e.g., in manual entry flow)
export const validateRegistrationFormatAny = (value: string): boolean => {
    if (!value.trim()) {
        return false;
    }

    const cleanValue = value.replace(/\s/g, '').toUpperCase();

    // Check if length is reasonable (max 7 characters)
    if (cleanValue.length > 7) {
        return false;
    }

    // Patterns for Nordic registration numbers (no dashes allowed)
    const patterns = [
        /^[A-Z]{3}\d{3}$/, // SE: ABC123
        /^[A-Z]{3}\d{2}[A-Z]$/, // SE: ABC12A or XXX12X (three letters, two digits, one letter)
        /^[A-Z]{2}\d{5}$/, // DK/NO: AB12345
        /^[A-Z]{2}\d{4}[A-Z]$/, // Some variations
        /^[A-Z]\d{5}[A-Z]$/, // Some variations
    ];

    return patterns.some((pattern) => pattern.test(cleanValue));
}; 